import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';
import { MoreHorizontal } from '../../icons';

/**
 * Overflow menu. Renders a trigger (default `···` icon button) and a popover list.
 * items: [{ label, icon, onSelect, danger, disabled, hidden, href }]
 * Falsy items are skipped so callers can do `cond && {...}`.
 */
const Menu = ({
  items,
  trigger,
  align = 'right',
  label = 'More actions',
  className,
  triggerClassName,
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  const close = () => {
    setOpen(false);
    onOpenChange && onOpenChange(false);
  };

  const toggle = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (open) {
      close();
      return;
    }
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const width = 224;
      const left = align === 'right' ? Math.max(8, Math.min(r.right - width, vw - width - 8)) : Math.max(8, Math.min(r.left, vw - width - 8));
      // Open upwards if we are near the bottom of the viewport.
      const openUp = r.bottom + 8 + 44 * Math.min(items.filter(Boolean).length, 6) > vh - 80;
      setPos({ left, top: openUp ? undefined : r.bottom + 6, bottom: openUp ? vh - r.top + 6 : undefined, width });
    }
    setOpen(true);
    onOpenChange && onOpenChange(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (popRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const visible = (items || []).filter((it) => it && !it.hidden);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={toggle}
        className={cn(
          trigger
            ? ''
            : 'w-9 h-9 inline-flex items-center justify-center rounded-lg text-secondary hover:text-primary hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
          triggerClassName,
          className
        )}
      >
        {trigger || <MoreHorizontal className="w-5 h-5" />}
      </button>

      {open &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={popRef}
            role="menu"
            className="fixed z-[120] bg-card border border-line rounded-xl shadow-pop py-1.5 animate-pop-in"
            style={pos || { top: 0, left: 0, width: 224 }}
            onClick={(e) => e.stopPropagation()}
          >
            {visible.map((it, i) => {
              const Icon = it.icon;
              const cls = cn(
                'w-full flex items-center gap-3 px-3.5 h-11 text-[15px] text-left transition-colors',
                it.danger ? 'text-danger hover:bg-danger-soft' : 'text-primary hover:bg-white/5',
                it.disabled && 'opacity-40 cursor-not-allowed hover:bg-transparent'
              );
              const inner = (
                <>
                  {Icon && <Icon className="w-[18px] h-[18px] flex-shrink-0" />}
                  <span className="flex-1 truncate">{it.label}</span>
                </>
              );
              if (it.href) {
                return (
                  <a
                    key={i}
                    role="menuitem"
                    href={it.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cls}
                    onClick={() => close()}
                  >
                    {inner}
                  </a>
                );
              }
              return (
                <button
                  key={i}
                  role="menuitem"
                  type="button"
                  disabled={it.disabled}
                  className={cls}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (it.disabled) return;
                    close();
                    it.onSelect && it.onSelect();
                  }}
                >
                  {inner}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
};

export default Menu;
